const pastelColors = [
    "bg-pink-100/90", "bg-blue-100/90", "bg-green-100/90",
    "bg-yellow-100/90", "bg-purple-100/90", "bg-orange-100/90", "bg-indigo-100/90"
];

// `flip` is owned by App.js rather than held in local state here. Cards unmount
// when they scroll far enough offscreen (see LazyCardWrapper), so local flip
// state would silently reset every time a card left and re-entered the viewport.
export default function FlashCard({ card, index, isRead, onToggleRead, flip, onFlip }) {
    const handleCardClick = (e) => {
        // If clicking the read-status checkbox/badge, don't flip the card
        if (e.target.closest(".read-toggle")) {
            e.stopPropagation();
            onToggleRead(!isRead);
            return;
        }

        const newFlip = !flip;
        onFlip(newFlip);

        // Auto-mark as read when flipped to show answer
        if (newFlip && !isRead) {
            onToggleRead(true);
        }
    };

    return (
        // `perspective` and `preserve-3d` are applied only while a card is
        // flipped. Both force a dedicated compositor layer, and an unflipped
        // card has nothing to project — leaving them on every card meant
        // hundreds of GPU layers at once, which is what exhausted memory on
        // mobile and triggered "Aw, Snap".
        <div className={`no-copy cursor-pointer h-full w-full ${flip ? "perspective" : ""}`} onClick={handleCardClick} onContextMenu={(e) => e.preventDefault()}>
            <div className={`relative w-full h-full transition-transform duration-500 ${flip ? "transform-style preserve-3d rotate-y-180" : ""}`}>
                {/* FRONT */}
                <div className={`absolute w-full h-full backface-hidden rounded-2xl shadow-sm border border-slate-200/60 p-5 flex flex-col justify-center items-center text-center ${pastelColors[index % pastelColors.length]}`}>
                    {/* Read Checkbox (Front) */}
                    <button
                        type="button"
                        className="read-toggle u-tap-halo absolute top-2 right-2 text-ink-500 hover:text-action-text transition p-2.5"
                        title={isRead ? "Mark as unread" : "Mark as read"}
                    >
                        {isRead ? (
                            <span className="text-xl">✅</span>
                        ) : (
                            <span className="w-5 h-5 rounded-full border-2 border-slate-400 block hover:border-indigo-500" />
                        )}
                    </button>

                    <p className="text-base font-semibold leading-snug text-slate-800 px-2">{card.front}</p>
                    <span className="text-[12px] mt-4 opacity-50 uppercase tracking-wider font-semibold">Tap to Reveal</span>
                </div>

                {/* BACK — always mounted. Deferring this until `flip` flips was
                    tried and reverted: the front face is `backface-hidden`, so
                    the back must already exist when the rotation starts or the
                    first half of the animation reveals an empty card. */}
                <div className="absolute w-full h-full backface-hidden rotate-y-180 rounded-2xl shadow-md p-5 flex flex-col justify-between items-center text-center bg-white dark:bg-slate-800 border-2 border-indigo-200 dark:border-indigo-800">
                    {/* Read Checkbox (Back) */}
                    <button
                        type="button"
                        className="read-toggle u-tap-halo absolute top-2 right-2 text-ink-500 hover:text-action-text transition p-2.5"
                        title={isRead ? "Mark as unread" : "Mark as read"}
                    >
                        {isRead ? (
                            <span className="text-xl">✅</span>
                        ) : (
                            <span className="w-5 h-5 rounded-full border-2 border-slate-400 dark:border-slate-500 block hover:border-indigo-500" />
                        )}
                    </button>

                    {/* Question at the top */}
                    <div className="w-full text-left border-b border-indigo-50 dark:border-slate-700 pb-1.5 mb-1 pr-6">
                        <span className="text-[12px] uppercase font-bold text-ink-500 tracking-wider block">Question</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate block">{card.front}</span>
                    </div>

                    {/* Answer in center. The card is a fixed height, so a long
                        answer plus a hook can overflow — it scrolls, and the
                        gradient below marks the cut-off so it looks scrollable. */}
                    <div className="relative flex-1 w-full my-1 min-h-0">
                        {/* `justify-center` on a scroll container clips the top of
                            overflowing content and makes it unreachable, so the
                            inner block is centred with `my-auto` instead — that
                            collapses to zero once the content is taller than the
                            box, leaving the whole answer scrollable. */}
                        <div className="h-full w-full overflow-y-auto px-0.5 flex flex-col">
                            <div className="my-auto w-full">
                                <p className="text-sm font-bold text-indigo-950 dark:text-indigo-200 whitespace-pre-line leading-snug">
                                    {card.back}
                                </p>
                                {card.image && (
                                    <img
                                        src={process.env.PUBLIC_URL + card.image}
                                        alt={card.imageAlt || ""}
                                        loading="lazy"
                                        className="w-12 h-12 mx-auto mt-3 shrink-0"
                                    />
                                )}
                                {/* Optional memory aid: analogy, mnemonic or "why it matters" */}
                                {card.hook && (
                                    <p className="mt-2.5 text-[14px] leading-snug text-amber-900 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/70 dark:border-amber-900/60 rounded-lg px-2.5 py-1.5">
                                        <span aria-hidden="true">💡 </span>{card.hook}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="pointer-events-none absolute bottom-0 inset-x-0 h-4 bg-gradient-to-t from-white dark:from-slate-800 to-transparent" />
                    </div>

                    {/* Explicit colours rather than opacity: faded white on the
                        dark card back was almost unreadable. */}
                    <span className="text-[12px] shrink-0 text-ink-500 uppercase tracking-wider font-semibold">Tap to Flip Back</span>
                </div>
            </div>
        </div>
    );
}